import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { getExceptionQueue } from './api';
import { AssignModal } from './AssignModal';
import {
  CarrierPolicyLink,
  CARRIER_POLICY_COLUMN_HEADER,
  formatDuration,
  GridCell,
  LifecycleChip,
  ProductChip,
} from './shared-cells';
import { durationFilterColumnProps, managerTabGridProps } from './shared-grid';
import { ExceptionItem, MANAGER_TAB_DEFAULT_PAGE_SIZE } from './types';
import { useGridView } from './view-state';

// Shared row-band sx
const rowBandSx = {
  '& .row-amber': {
    bgcolor: 'rgba(255, 152, 0, 0.08)',
    '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.14)' },
  },
  '& .row-red': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.14)' },
  },
};

const EXCEPTION_QUEUE_OVERRIDE_REASONS = [
  'Bandwidth override',
  'Specialization override',
  'Authority limit override',
  'Continuity override',
  'Manager judgement',
  'Other',
];

// ─── Exception Queue tab ──────────────────────────────────────────────────────

export function ExceptionQueueTab() {
  const [exceptionSelectedIds, setExceptionSelectedIds] = useState<Set<number>>(
    new Set(),
  );
  const [assignModalIds, setAssignModalIds] = useState<number[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: MANAGER_TAB_DEFAULT_PAGE_SIZE,
  });
  // Filter and sort are remembered while the app is open (see useGridView).
  const view = useGridView('exception-queue');
  const [sortModel, setSortModel] = useState<GridSortModel>(
    view.restored.sortModel ?? [],
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    view.restored.filterModel ?? { items: [] },
  );

  const activeSortItem = sortModel[0];
  const activeFilterItem = filterModel.items.find(
    item =>
      item.value !== undefined &&
      item.value !== null &&
      String(item.value).trim() !== '',
  );
  const activeFilterValue =
    activeFilterItem?.value === undefined
      ? undefined
      : String(activeFilterItem.value);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'manager-tab-exception-queue',
      paginationModel.page,
      paginationModel.pageSize,
      activeSortItem?.field,
      activeSortItem?.sort,
      activeFilterItem?.field,
      activeFilterValue,
    ],
    queryFn: () =>
      getExceptionQueue({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        sortBy: activeSortItem?.field,
        sortOrder: activeSortItem?.sort ?? undefined,
        filterBy: activeFilterItem?.field,
        filterValue: activeFilterValue,
      }),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    view.saveSort(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    view.saveFilter(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const openAssign = (ids: number[]) => {
    setAssignModalIds(ids);
    setAssignOpen(true);
  };

  const exceptionColumns: GridColDef<ExceptionItem>[] = [
    {
      field: 'carrierPolicyNumber',
      headerName: CARRIER_POLICY_COLUMN_HEADER,
      flex: 0.8,
      minWidth: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ row }) => (
        <CarrierPolicyLink
          applicationId={row.applicationId}
          carrierPolicyNumber={row.carrierPolicyNumber}
          termPolicyId={row.termPolicyId}
        />
      ),
    },
    {
      field: 'product',
      headerName: 'PRODUCT',
      flex: 0.8,
      minWidth: 110,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ value }) => <ProductChip value={value} />,
    },
    {
      field: 'lifecycle',
      headerName: 'LIFECYCLE',
      flex: 0.9,
      minWidth: 130,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ value }) => <LifecycleChip value={value} />,
    },
    {
      field: 'ageHours',
      headerName: 'UNASSIGNED FOR',
      flex: 1,
      minWidth: 150,
      type: 'number',
      ...durationFilterColumnProps,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ value }) => (
        <GridCell>
          <Typography variant="body2">
            {formatDuration(value as number)}
          </Typography>
        </GridCell>
      ),
    },
    {
      field: 'blockingReason',
      headerName: 'BLOCKING REASON',
      flex: 1.3,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ value }) => (
        <GridCell>
          <Typography variant="body2" color="text.secondary">
            {value ?? '—'}
          </Typography>
        </GridCell>
      ),
    },
  ];

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Failed to load exception queue: {(error as Error)?.message}
      </Alert>
    );
  }

  const exceptions = data?.exceptions ?? [];
  const totalCount = data?.totalCount ?? 0;

  const currentPageIds = new Set(exceptions.map(e => e.applicationId));
  const currentPageSelectedIds = exceptions
    .map(e => e.applicationId)
    .filter(id => exceptionSelectedIds.has(id));

  const handleSelectionChange = (newIds: GridRowSelectionModel) => {
    setExceptionSelectedIds(prev => {
      const next = new Set(prev);
      for (const id of currentPageIds) {
        next.delete(id);
      }
      for (const id of newIds as number[]) {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <Box>
      {/* ── Unassigned cases ── */}
      <Typography variant="h6" fontWeight={600}>
        Unassigned cases
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Cases the engine could not auto-assign ·{' '}
        {activeSortItem
          ? `sorted by ${activeSortItem.field} ${activeSortItem.sort}`
          : 'sorted by unassigned time asc'}{' '}
        · {totalCount} total
      </Typography>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="caption" color="text.secondary">
          {exceptionSelectedIds.size > 0
            ? `${exceptionSelectedIds.size} case${exceptionSelectedIds.size !== 1 ? 's' : ''} selected`
            : 'Select cases to assign'}
        </Typography>
        {exceptionSelectedIds.size > 0 && (
          <Button
            variant="contained"
            size="small"
            onClick={() => openAssign([...exceptionSelectedIds])}
          >
            Assign to UW ({exceptionSelectedIds.size})
          </Button>
        )}
      </Box>

      <DataGrid
        rows={exceptions}
        columns={exceptionColumns}
        getRowId={r => r.applicationId}
        checkboxSelection
        disableRowSelectionOnClick
        {...managerTabGridProps}
        initialState={view.initialState}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowSelectionModel={currentPageSelectedIds}
        onRowSelectionModelChange={handleSelectionChange}
        getRowClassName={({ row }) =>
          row.ageBand !== 'neutral' ? `row-${row.ageBand}` : ''
        }
        autoHeight
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        loading={isLoading}
        sx={{ bgcolor: 'background.paper', ...rowBandSx }}
      />

      <AssignModal
        applicationIds={assignModalIds}
        open={assignOpen}
        invalidateKey="manager-tab-exception-queue"
        overrideReasons={EXCEPTION_QUEUE_OVERRIDE_REASONS}
        onAssigned={assignedIds =>
          setExceptionSelectedIds(prev => {
            const next = new Set(prev);
            for (const id of assignedIds) {
              next.delete(id);
            }
            return next;
          })
        }
        onClose={(clearSelection?: boolean) => {
          setAssignOpen(false);
          if (clearSelection) {
            setExceptionSelectedIds(new Set());
          }
        }}
      />
    </Box>
  );
}
